import {
  Button,
  ColumnLayout,
  Container,
  CopyToClipboard,
  Flashbar,
  Header,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import React from "react";
import { CodeView } from "@amzn/awsui-code-view";
import { SummaryItem } from "./SummaryItem";
import { useSummaryData } from "../../hooks/useSummaryData";
import ExternalLinks from "../ExternalLinks";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../../types/documents";
import { AccountingEventsGroup } from "../../types/events";
import HealthStatusIndicator from "../common/HealthStatusIndicator";
import { DlqInfo } from "../../hooks/useFetchDlqInfo";
import DlqStatusIndicator from "../dlq/DlqStatusIndicator";
import { screamingSnakeToTitleCase } from "../../helpers/dbdUtils";
import useFolders from "../../hooks/useFolders";
import { useFolderUI } from "../../hooks/useFolderUI";
import { FolderModal } from "../folders/FolderModal";
import { SavedSearchItem } from "../../types/common";
import { CanonicalBusinessEvent } from "../../types/cbes";

interface SummaryProps {
  eventData: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
  )[];
  dlqInfo: DlqInfo;
  cbeData?: CanonicalBusinessEvent[];
}

export default function Summary({
  eventData,
  dlqInfo,
  cbeData = [],
}: SummaryProps) {
  const { folders, addTransactions, saveFolder } = useFolders();
  const {
    isModalVisible,
    isFolderModalVisible,
    selectedFolderId,
    folderName,
    error,
    setIsModalVisible,
    setSelectedFolderId,
    setFolderName,
    setNotification,
    handleModalDismiss,
    handleCreateFolder,
    handleFolderModalDismiss,
    handleSaveFolder,
    getFlashbarItems,
    flashbarStyles,
  } = useFolderUI(true);

  const {
    stage,
    orderId,
    countryCode,
    healthStatus,
    flowSteps,
    orderDate,
    glProductGroup,
    consumptionType,
    fulfillmentType,
    paymentMethod,
    isFreeTrial,
    isDocumentBundle,
    isMgm,
    getGlMapping,
    lastItem,
    isHealthLoading,
  } = useSummaryData(eventData, dlqInfo);

  const formattedConsumptionType = consumptionType
    ? screamingSnakeToTitleCase(consumptionType.toUpperCase())
    : null;

  const handleFolderSelect = () => {
    if (selectedFolderId && orderId) {
      const newTransaction: SavedSearchItem = {
        date: new Date().toISOString(),
        id: orderId,
        stage: stage || "",
        status: healthStatus,
        country: countryCode || "",
        gl: String(glProductGroup || ""),
        usecase: "",
        paymentMethod: paymentMethod || "",
        labels: [],
        consumptionTypes: [],
      };

      const result = addTransactions(selectedFolderId, [newTransaction]);

      setNotification({
        type: result.success ? "success" : "error",
        message: result.message,
      });
      handleModalDismiss();
    }
  };

  const generateShadowTestCommand = () => {
    try {
      const accountingEventsGroup = eventData.find(
        (item): item is AccountingEventsGroup => "accountingEvents" in item
      );

      const firstEvent = accountingEventsGroup
        ? Object.values(accountingEventsGroup.accountingEvents)[0]
        : null;

      const accountingUniqueId = firstEvent?.accountingUniqueId || "";
      const subscriptionId = firstEvent?.subscriptionId || "";
      const subscriptionPeriod = firstEvent?.subscriptionPeriod || "";
      const digitalOrderID = firstEvent?.orderId || "";
      const deliveryPacketItemId = firstEvent?.deliveryPacketItemId || "";
      const financeTagAuthReferenceId =
        firstEvent?.financeTagAuthReferenceId || "";

      return `brazil-build shadow-test --args="-m ${countryCode} -gl ${glProductGroup} -o ${orderId}|${accountingUniqueId}|${subscriptionId}|${subscriptionPeriod}|${deliveryPacketItemId}|${digitalOrderID}|${financeTagAuthReferenceId}|"`;
    } catch (error) {
      // Fallback command with only basic attributes
      return `brazil-build shadow-test --args="-m ${countryCode} -gl ${glProductGroup} -o ${orderId}"`;
    }
  };

  return (
    <>
      <div style={flashbarStyles}>
        <Flashbar items={getFlashbarItems()} />
      </div>
      <Container
        header={
          <Header
            variant="h1"
            description="Summary"
            actions={
              <SpaceBetween direction="horizontal" size="xxs">
                <Popover
                  dismissButton={false}
                  fixedWidth={false}
                  position="bottom"
                  size="large"
                  triggerType="custom"
                  content={
                    <>
                      Copy this command to run shadow testing locally
                      <CodeView
                        content={generateShadowTestCommand()}
                        actions={
                          <CopyToClipboard
                            copyButtonAriaLabel="Copy query"
                            copyErrorText="Query failed to copy"
                            copySuccessText="Query copied"
                            textToCopy={generateShadowTestCommand()}
                          />
                        }
                      />
                    </>
                  }
                >
                  <Button>Get Shadow Test Command</Button>
                </Popover>
                <Button onClick={() => setIsModalVisible(true)}>
                  Add to Folder
                </Button>
              </SpaceBetween>
            }
          >
            {orderId}
          </Header>
        }
      >
        <ColumnLayout columns={5} borders="vertical">
          <SummaryItem label="Stage" value={stage} />
          <SummaryItem
            label="GL"
            value={glProductGroup ? getGlMapping(glProductGroup) : "-"}
          />
          <SummaryItem label="Country" value={countryCode} />
          {paymentMethod && (
            <SummaryItem
              label="Payment"
              value={
                isFreeTrial ? `Free Trial (${paymentMethod})` : paymentMethod
              }
            />
          )}
          <SummaryItem
            label="Order Date (UTC)"
            value={
              orderDate
                ? new Date(orderDate).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: "UTC",
                  })
                : "-"
            }
          />
          {formattedConsumptionType && (
            <SummaryItem label="Consumption" value={formattedConsumptionType} />
          )}
          {fulfillmentType && (
            <SummaryItem
              label="Fulfillment"
              value={screamingSnakeToTitleCase(fulfillmentType)}
            />
          )}
          {isDocumentBundle !== undefined && (
            <SummaryItem
              label="Bundle"
              value={isDocumentBundle ? "Yes" : "No"}
            />
          )}
          <SummaryItem
            label="Status"
            value={
              <HealthStatusIndicator
                healthStatus={healthStatus}
                flowSteps={flowSteps}
                isLoading={isHealthLoading}
                eventData={eventData}
                cbeData={cbeData}
              />
            }
          />
          <SummaryItem
            label="DLQ Status"
            value={
              <DlqStatusIndicator
                status={dlqInfo.status}
                dlqItems={dlqInfo.dlqItems}
              />
            }
          />
          <SummaryItem
            label="External Links"
            value={<ExternalLinks {...lastItem} />}
          />
        </ColumnLayout>
      </Container>

      <FolderModal
        isModalVisible={isModalVisible}
        isFolderModalVisible={isFolderModalVisible}
        isShareModalVisible={false}
        selectedFolderId={selectedFolderId}
        folderName={folderName}
        error={error}
        folders={folders}
        sharingFolder={null}
        onModalDismiss={handleModalDismiss}
        onCreateFolder={handleCreateFolder}
        onFolderSelect={handleFolderSelect}
        onFolderModalDismiss={handleFolderModalDismiss}
        onShareModalDismiss={() => {}}
        onSaveFolder={() => handleSaveFolder(saveFolder)}
        setSelectedFolderId={setSelectedFolderId}
        setFolderName={setFolderName}
      />
    </>
  );
}
